/**
 * Trade Analytics Modal
 *
 * Displays key trade analytics from the trade-analyzer service in a
 * compact overlay. Features account filtering, equity curve, daily P&L,
 * session/day breakdowns, and recent trades.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getBackendUrl } from '../config/ports';
import './TradeAnalyticsModal.css';

// ---------- Types ----------

interface AccountInfo {
  name: string;
  trades: number;
  pnl: number;
}

interface BreakdownEntry {
  trades: number;
  pnl: number;
  winRate: number;
}

interface DailyStats {
  date: string;
  totalPnl: number;
  netPnl: number;
  tradeCount: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  largestWin: number;
  largestLoss: number;
}

interface AnalyticsReport {
  totalTrades: number;
  totalPnl: number;
  totalNetPnl: number;
  winRate: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  avgRR: number;
  largestWin: number;
  largestLoss: number;
  maxConsecutiveWins: number;
  maxConsecutiveLosses: number;
  maxDrawdown: number;
  dailyStats: DailyStats[];
  bySession: Record<string, BreakdownEntry>;
  byDayOfWeek: Record<string, BreakdownEntry>;
}

interface Trade {
  id: string;
  date: string;
  symbol: string;
  side: string;
  pnl: number;
  netPnl: number;
  points: number;
  duration: number;
  accountName: string;
  session: string;
}

// ---------- Filter persistence ----------

const FILTER_STORAGE_KEY = 'trade-analytics-filters';

interface SavedFilters {
  account: string;
  startDate: string;
  endDate: string;
}

function defaultDateRange(): { startDate: string; endDate: string } {
  const end = new Date().toISOString().split('T')[0];
  const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  return { startDate: start, endDate: end };
}

function loadFilters(): SavedFilters {
  try {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore corrupt data */ }
  const { startDate, endDate } = defaultDateRange();
  return { account: '', startDate, endDate };
}

function saveFilters(filters: SavedFilters): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch { /* storage full / disabled */ }
}

// ---------- Helpers ----------

function fmtCurrency(v: number): string {
  const sign = v >= 0 ? '' : '-';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

function pnlClass(v: number): string {
  if (v > 0) return 'positive';
  if (v < 0) return 'negative';
  return '';
}

// ---------- Props ----------

interface TradeAnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ---------- Component ----------

const TradeAnalyticsModal: React.FC<TradeAnalyticsModalProps> = ({ isOpen, onClose }) => {
  const [savedFilters] = useState(loadFilters);
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>(savedFilters.account);
  const [startDate, setStartDate] = useState(savedFilters.startDate);
  const [endDate, setEndDate] = useState(savedFilters.endDate);
  const [showFilters, setShowFilters] = useState(false);
  const [report, setReport] = useState<AnalyticsReport | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const backendUrl = getBackendUrl();

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/trade-analyzer/accounts`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: AccountInfo[] = await res.json();
      setAccounts(data);
    } catch {
      // Silently fail for accounts — modal still works without filter
      setAccounts([]);
    }
  }, [backendUrl]);

  const fetchData = useCallback(async (account?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (account) params.set('account', account);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const [reportRes, tradesRes] = await Promise.all([
        fetch(`${backendUrl}/api/trade-analyzer/report${qs}`),
        fetch(`${backendUrl}/api/trade-analyzer/trades${qs}`),
      ]);

      if (!reportRes.ok) throw new Error(`Report: HTTP ${reportRes.status}`);
      if (!tradesRes.ok) throw new Error(`Trades: HTTP ${tradesRes.status}`);

      const reportData: AnalyticsReport = await reportRes.json();
      const tradesData = await tradesRes.json();

      setReport(reportData);
      // trades endpoint may return { trades: [...] } or just [...]
      const tradesList = Array.isArray(tradesData) ? tradesData : tradesData.trades ?? [];
      setTrades(tradesList);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load analytics: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, startDate, endDate]);

  // Persist filters to localStorage
  useEffect(() => {
    saveFilters({ account: selectedAccount, startDate, endDate });
  }, [selectedAccount, startDate, endDate]);

  // Fetch accounts + data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
      fetchData(selectedAccount || undefined);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when account changes (but not on initial mount)
  useEffect(() => {
    if (isOpen && accounts.length > 0) {
      fetchData(selectedAccount || undefined);
    }
  }, [selectedAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when date filters change (only when both set or both empty)
  useEffect(() => {
    if (!isOpen) return;
    if ((startDate && endDate) || (!startDate && !endDate)) {
      fetchData(selectedAccount || undefined);
    }
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch(`${backendUrl}/api/trade-analyzer/refresh`, { method: 'POST' });
      // Wait briefly for data to rebuild, then re-fetch
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await fetchData(selectedAccount || undefined);
    } catch {
      // Refresh failed silently — user can try again
    } finally {
      setRefreshing(false);
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div className="tan-backdrop" onClick={onClose}>
      <div className="tan-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tan-header">
          <div className="tan-header-left">
            <h2 className="tan-title">Trade Analytics</h2>
          </div>
          <div className="tan-header-right">
            {accounts.length > 0 && (
              <select
                className="tan-account-select"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                <option value="">All Accounts</option>
                {accounts.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.name} ({a.trades} trades)
                  </option>
                ))}
              </select>
            )}
            <button
              className="tan-filter-toggle"
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters {showFilters ? '\u25BE' : '\u25B8'}
              {(startDate || endDate) && <span className="tan-filter-badge">{'\u25CF'}</span>}
            </button>

            {showFilters && (
              <div className="tan-date-filters">
                <input
                  type="date"
                  className="tan-date-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Start"
                />
                <span className="tan-date-separator">to</span>
                <input
                  type="date"
                  className="tan-date-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="End"
                />
                <button
                  className="tan-date-clear"
                  onClick={() => {
                    const { startDate: s, endDate: e } = defaultDateRange();
                    setStartDate(s);
                    setEndDate(e);
                  }}
                >
                  Reset
                </button>
              </div>
            )}
            <button className="tan-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {/* Content */}
        <div className="tan-content">
          {loading ? (
            <div className="tan-loading">Loading analytics...</div>
          ) : error ? (
            <div className="tan-error">{error}</div>
          ) : report ? (
            <>
              {/* Hero metrics */}
              <HeroMetrics report={report} />

              {/* Equity curve */}
              <EquityCurve dailyStats={report.dailyStats} />

              {/* Daily P&L bars */}
              <DailyPnLBars dailyStats={report.dailyStats} />

              {/* Breakdowns */}
              <div className="tan-breakdowns">
                <BreakdownTable title="By Session" data={report.bySession} />
                <BreakdownTable title="By Day of Week" data={report.byDayOfWeek} />
              </div>

              {/* Recent trades */}
              <RecentTrades trades={trades} />
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="tan-footer">
          <a
            className="tan-journal-link"
            href="https://journal.ginuga.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Full Journal
            <span className="tan-link-arrow">{'\u2197'}</span>
          </a>
          <button
            className="tan-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

// ---------- Hero Metrics ----------

const HeroMetrics: React.FC<{ report: AnalyticsReport }> = ({ report }) => (
  <div className="tan-metrics">
    <div className="tan-metric">
      <div className={`tan-metric-value ${pnlClass(report.totalNetPnl)}`}>
        {fmtCurrency(report.totalNetPnl)}
      </div>
      <div className="tan-metric-label">Net P&L</div>
    </div>
    <div className="tan-metric">
      <div className={`tan-metric-value ${report.winRate >= 50 ? 'positive' : 'negative'}`}>
        {fmtPct(report.winRate)}
      </div>
      <div className="tan-metric-label">Win Rate</div>
    </div>
    <div className="tan-metric">
      <div className={`tan-metric-value ${report.profitFactor >= 1 ? 'positive' : 'negative'}`}>
        {report.profitFactor.toFixed(2)}
      </div>
      <div className="tan-metric-label">Profit Factor</div>
    </div>
    <div className="tan-metric">
      <div className="tan-metric-value neutral">{report.totalTrades}</div>
      <div className="tan-metric-label">Trades</div>
    </div>
    <div className="tan-metric">
      <div className="tan-metric-value positive">{fmtCurrency(report.avgWin)}</div>
      <div className="tan-metric-label">Avg Win</div>
    </div>
    <div className="tan-metric">
      <div className="tan-metric-value negative">{fmtCurrency(report.avgLoss)}</div>
      <div className="tan-metric-label">Avg Loss</div>
    </div>
  </div>
);

// ---------- Equity Curve ----------

const EquityCurve: React.FC<{ dailyStats: DailyStats[] }> = ({ dailyStats }) => {
  if (dailyStats.length === 0) return null;

  const sorted = [...dailyStats].sort((a, b) => a.date.localeCompare(b.date));

  // Build cumulative equity
  let cumulative = 0;
  const points = sorted.map((d) => {
    cumulative += d.netPnl;
    return { date: d.date, equity: cumulative };
  });

  const maxEquity = Math.max(...points.map((p) => p.equity));
  const minEquity = Math.min(...points.map((p) => p.equity));
  const range = maxEquity - minEquity || 1;
  const finalPositive = cumulative >= 0;

  // Build SVG path
  const chartWidth = 100;
  const chartHeight = 100;
  const pathPoints = points.map((p, i) => {
    const x = (i / Math.max(points.length - 1, 1)) * chartWidth;
    const y = chartHeight - ((p.equity - minEquity) / range) * chartHeight;
    return `${x},${y}`;
  });

  const linePath = `M${pathPoints.join(' L')}`;
  const areaPath = `${linePath} L${chartWidth},${chartHeight} L0,${chartHeight} Z`;
  const strokeColor = finalPositive ? '#22c55e' : '#ef4444';
  const fillColor = finalPositive ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)';

  return (
    <div className="tan-chart-section">
      <div className="tan-section-title">Equity Curve</div>
      <div className="tan-equity-chart">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="none"
          className="tan-equity-svg"
        >
          <path d={areaPath} fill={fillColor} />
          <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className="tan-equity-labels">
          <span>{fmtCurrency(maxEquity)}</span>
          <span>{fmtCurrency(minEquity)}</span>
        </div>
      </div>
    </div>
  );
};

// ---------- Daily P&L Bars ----------

const DailyPnLBars: React.FC<{ dailyStats: DailyStats[] }> = ({ dailyStats }) => {
  if (dailyStats.length === 0) return null;

  const sorted = [...dailyStats].sort((a, b) => a.date.localeCompare(b.date));
  const maxAbs = Math.max(...sorted.map((d) => Math.abs(d.netPnl)), 1);

  return (
    <div className="tan-chart-section">
      <div className="tan-section-title">Daily P&L</div>
      <div className="tan-daily-bars">
        {sorted.map((d) => {
          const heightPct = (Math.abs(d.netPnl) / maxAbs) * 100;
          const isPositive = d.netPnl >= 0;
          return (
            <div key={d.date} className="tan-bar-col" title={`${d.date}: ${fmtCurrency(d.netPnl)}`}>
              <div className="tan-bar-space">
                <div
                  className={`tan-bar ${isPositive ? 'green' : 'red'}`}
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                />
              </div>
              <div className="tan-bar-label">{d.date.slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Breakdown Table ----------

const BreakdownTable: React.FC<{
  title: string;
  data: Record<string, BreakdownEntry>;
}> = ({ title, data }) => {
  const entries = Object.entries(data).sort((a, b) => b[1].trades - a[1].trades);

  if (entries.length === 0) {
    return (
      <div className="tan-breakdown">
        <div className="tan-section-title">{title}</div>
        <div className="tan-no-data">No data</div>
      </div>
    );
  }

  return (
    <div className="tan-breakdown">
      <div className="tan-section-title">{title}</div>
      <div className="tan-table-wrap">
        <table className="tan-table">
          <thead>
            <tr>
              <th>{title.replace('By ', '')}</th>
              <th className="right">Trades</th>
              <th className="right">P&L</th>
              <th className="right">Win%</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, val]) => (
              <tr key={key}>
                <td>{key}</td>
                <td className="right">{val.trades}</td>
                <td className={`right ${pnlClass(val.pnl)}`}>{fmtCurrency(val.pnl)}</td>
                <td className={`right ${val.winRate >= 50 ? 'positive' : 'negative'}`}>
                  {fmtPct(val.winRate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Recent Trades ----------

const RecentTrades: React.FC<{ trades: Trade[] }> = ({ trades }) => {
  const recent = trades.slice(0, 10);

  if (recent.length === 0) return null;

  return (
    <div className="tan-chart-section">
      <div className="tan-section-title">Recent Trades</div>
      <div className="tan-table-wrap">
        <table className="tan-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Symbol</th>
              <th>Side</th>
              <th className="right">P&L</th>
              <th className="right">Points</th>
              <th className="right">Duration</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((t) => (
              <tr key={t.id}>
                <td>{t.date}</td>
                <td>{t.symbol}</td>
                <td className={t.side === 'LONG' ? 'positive' : 'negative'}>{t.side}</td>
                <td className={`right ${pnlClass(t.netPnl)}`}>{fmtCurrency(t.netPnl)}</td>
                <td className="right">{t.points.toFixed(2)}</td>
                <td className="right">{fmtDuration(t.duration)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TradeAnalyticsModal;
